// Typed client for the /pricing/* endpoints, built on the app's `lib/request` fetch wrapper
// (the same client the Insights lexicon / differential-scenario pages use).
import request from 'lib/request';
import type {
  Contract,
  Curve,
  CurveExpiry,
  ListResponse,
  MarketDataQuery,
  MarketDataResponse,
  PricingEnums,
  PricingObject,
  SavedView,
  SavedViewConfig,
  Transformation,
} from './types';

export interface ValidationErrors {
  errors: Record<string, string[]>;
}

// A create/update failure surfaced as a field->messages map from the backend 422.
export class PricingApiError extends Error {
  errors: Record<string, string[]>;

  constructor(errors: Record<string, string[]>) {
    super(
      Object.entries(errors)
        .map(([f, m]) => `${f}: ${m.join(', ')}`)
        .join('; ') || 'Request failed'
    );
    this.errors = errors;
  }
}

function unwrapGet<T>(res: T | { error: string }): T {
  if (res && typeof res === 'object' && 'error' in res)
    throw new Error((res as { error: string }).error);
  return res as T;
}

function unwrapWrite<T>(res: {
  type: string;
  data?: unknown;
  message?: string;
}): T {
  if (res.type === 'success') return res.data as T;
  if (res.type === 'error')
    throw new PricingApiError((res.data as ValidationErrors)?.errors ?? {});
  throw new Error(res.message || 'Request failed');
}

const ctrl = () => new AbortController();

export const pricingApi = {
  // --- enums / meta ---
  enums: () =>
    request.get<PricingEnums>('/pricing/enums', ctrl()).then(unwrapGet),
  asOfDates: () =>
    request
      .get<ListResponse<string>>('/pricing/as_of_dates', ctrl())
      .then(unwrapGet),

  // --- curves ---
  listCurves: () =>
    request
      .post<ListResponse<Curve>, ValidationErrors>(
        '/queries/pricing/curves',
        { query: { page_size: 1000 } },
        ctrl()
      )
      .then((r) => unwrapWrite<ListResponse<Curve>>(r)),
  getCurve: (id: number) =>
    request.get<Curve>(`/pricing/curves/${id}`, ctrl()).then(unwrapGet),
  createCurve: (curve: Partial<Curve>) =>
    request
      .post<Curve, ValidationErrors>('/pricing/curves', { curve }, ctrl())
      .then((r) => unwrapWrite<Curve>(r)),
  updateCurve: (id: number, curve: Partial<Curve>) =>
    request
      .patch<Curve, ValidationErrors>(
        `/pricing/curves/${id}`,
        { curve },
        ctrl()
      )
      .then((r) => unwrapWrite<Curve>(r)),
  deleteCurve: (id: number) =>
    request
      .deleteReq<unknown, ValidationErrors>(
        `/pricing/curves/${id}`,
        null,
        ctrl()
      )
      .then((r) => unwrapWrite<unknown>(r)),
  curveExpiries: (id: number) =>
    request
      .get<{ curveId: number; results: CurveExpiry[] }>(
        `/pricing/curves/${id}/expiries`,
        ctrl()
      )
      .then(unwrapGet),

  // --- contracts ---
  listContracts: (text?: string) =>
    request
      .post<ListResponse<Contract>, ValidationErrors>(
        '/queries/pricing/contracts',
        { query: { page_size: 1000, ...(text ? { text } : {}) } },
        ctrl()
      )
      .then((r) => unwrapWrite<ListResponse<Contract>>(r)),
  getContract: (id: number) =>
    request.get<Contract>(`/pricing/contracts/${id}`, ctrl()).then(unwrapGet),
  createContract: (contract: Partial<Contract>) =>
    request
      .post<Contract, ValidationErrors>(
        '/pricing/contracts',
        { contract },
        ctrl()
      )
      .then((r) => unwrapWrite<Contract>(r)),
  updateContract: (id: number, contract: Partial<Contract>) =>
    request
      .patch<Contract, ValidationErrors>(
        `/pricing/contracts/${id}`,
        { contract },
        ctrl()
      )
      .then((r) => unwrapWrite<Contract>(r)),
  deleteContract: (id: number) =>
    request
      .deleteReq<unknown, ValidationErrors>(
        `/pricing/contracts/${id}`,
        null,
        ctrl()
      )
      .then((r) => unwrapWrite<unknown>(r)),

  // --- transformations ---
  listTransformations: () =>
    request
      .post<ListResponse<Transformation>, ValidationErrors>(
        '/queries/pricing/transformations',
        { query: { page_size: 1000 } },
        ctrl()
      )
      .then((r) => unwrapWrite<ListResponse<Transformation>>(r)),
  getTransformation: (id: number) =>
    request
      .get<Transformation>(`/pricing/transformations/${id}`, ctrl())
      .then(unwrapGet),
  createTransformation: (transformation: Partial<Transformation>) =>
    request
      .post<Transformation, ValidationErrors>(
        '/pricing/transformations',
        { transformation },
        ctrl()
      )
      .then((r) => unwrapWrite<Transformation>(r)),
  updateTransformation: (id: number, transformation: Partial<Transformation>) =>
    request
      .patch<Transformation, ValidationErrors>(
        `/pricing/transformations/${id}`,
        { transformation },
        ctrl()
      )
      .then((r) => unwrapWrite<Transformation>(r)),
  deleteTransformation: (id: number) =>
    request
      .deleteReq<unknown, ValidationErrors>(
        `/pricing/transformations/${id}`,
        null,
        ctrl()
      )
      .then((r) => unwrapWrite<unknown>(r)),

  // --- pricing object engine ---
  assemble: (body: {
    asOfDate: string;
    curveIds?: number[];
    contractIds?: number[];
    transformationIds?: number[];
  }) =>
    request
      .post<PricingObject, ValidationErrors>('/pricing/objects', body, ctrl())
      .then((r) => unwrapWrite<PricingObject>(r)),

  // --- saved Price-Visualization views ---
  listSavedViews: () =>
    request
      .get<ListResponse<SavedView>>('/pricing/saved_views', ctrl())
      .then(unwrapGet),
  // config is sent as a JSON string so its camelCase inner keys survive rails-boost's key
  // underscoring (see SavedViewsController). Upserts by name.
  saveView: (name: string, config: SavedViewConfig) =>
    request
      .post<SavedView, ValidationErrors>(
        '/pricing/saved_views',
        { saved_view: { name, config: JSON.stringify(config) } },
        ctrl()
      )
      .then((r) => unwrapWrite<SavedView>(r)),
  deleteSavedView: (id: number) =>
    request
      .deleteReq<unknown, ValidationErrors>(
        `/pricing/saved_views/${id}`,
        null,
        ctrl()
      )
      .then((r) => unwrapWrite<unknown>(r)),

  // --- market-data visualization series (server-shaped) ---
  marketData: (query: MarketDataQuery) =>
    request
      .post<MarketDataResponse, ValidationErrors>(
        '/queries/pricing/market-data',
        { query },
        ctrl()
      )
      .then((r) => unwrapWrite<MarketDataResponse>(r)),
};
